import { useCallback, useEffect, useRef, useState } from 'react';
import { t, useLocale } from '../localization/adminText';
import { Badge, Banner, BlockStack, Button, DataTable, InlineGrid, InlineStack, Modal, Text, TextField } from './admin-ui';
import { exportRiskOrders, getRiskOrderRecords, previewRiskOrders, submitRiskOrders } from '../api/codRiskImports';
import { downloadRiskTemplate, readRiskFile, riskImportError, riskImportTime, riskOutcomeLabel, riskPoolLabel, riskRowStatus, sharedReviewLabel } from './CodRiskImportDialog.helpers';
import './CodRiskImportDialog.css';

export default function CodRiskImportDialog({ open, onClose, userToken }) {
  if (!open || !userToken) return null;
  // A changed shop must never retain another shop's preview or confirmation token.
  return <RiskImportSession key={userToken} onClose={onClose} userToken={userToken} />;
}

function RiskImportSession({ onClose, userToken }) {
  useLocale();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [file, setFile] = useState(null);
  const [sourceTimezone, setSourceTimezone] = useState('');
  const [sheetName, setSheetName] = useState('');
  const [headerRow, setHeaderRow] = useState('1');
  const [preview, setPreview] = useState(null);
  const [expired, setExpired] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [historyError, setHistoryError] = useState('');
  const [records, setRecords] = useState({ list: [], page: 1, last_page: 1 });
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const generation = useRef(0);
  const busyRef = useRef(false);
  useEffect(() => () => { generation.current++; }, []);

  useEffect(() => {
    let active = true;
    setRecordsLoading(true); setHistoryError(''); setRecords(value => ({ ...value, list: [] }));
    getRiskOrderRecords({ user_token: userToken, page, limit: 25 }).then(data => {
      if (active) setRecords(data);
    }).catch(e => { if (active) setHistoryError(riskImportError(e)); })
      .finally(() => { if (active) setRecordsLoading(false); });
    return () => { active = false; };
  }, [page, refresh, userToken]);

  useEffect(() => {
    const remaining = Date.parse(preview?.expires_at) - Date.now();
    setExpired(Boolean(preview) && (!Number.isFinite(remaining) || remaining <= 0));
    if (!preview || !Number.isFinite(remaining) || remaining <= 0) return undefined;
    const timer = setTimeout(() => setExpired(true), Math.min(remaining, 2147483647));
    return () => clearTimeout(timer);
  }, [preview]);

  const run = useCallback(async (operation, action) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const current = generation.current;
    const active = () => current === generation.current;
    setBusy(operation); setError('');
    try { await action(active); }
    catch (e) { if (active()) setError(riskImportError(e)); }
    finally { if (active()) { busyRef.current = false; setBusy(''); } }
  }, []);

  const changeImport = setter => value => {
    if (busyRef.current) return;
    generation.current++; setter(value); setPreview(null); setError(''); setNotice(null);
  };
  const chooseFile = event => {
    if (busyRef.current) return;
    generation.current++; setPreview(null); setNotice(null); setError(''); setFile(null);
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!/\.(csv|xlsx)$/i.test(selected.name) || selected.size > 5 * 1024 * 1024 || selected.size === 0) {
      setError('Choose a UTF-8 CSV or Excel (.xlsx) file no larger than 5 MB.'); return;
    }
    setFile(selected);
  };
  const download = () => run('download', async active => {
    if (startDate && endDate && startDate > endDate) throw new Error('The start date must be on or before the end date.');
    const data = await exportRiskOrders({ user_token: userToken, ...(startDate ? { start_date: startDate } : {}), ...(endDate ? { end_date: endDate } : {}) });
    if (active()) downloadRiskTemplate(data);
  });
  const createPreview = () => run('preview', async active => {
    setPreview(null); setNotice(null);
    if (!file) return;
    try { new Intl.DateTimeFormat('en', { timeZone: sourceTimezone.trim() }).format(); }
    catch { throw new Error('Enter a valid source time zone, such as Asia/Shanghai.'); }
    if (!sourceTimezone.trim()) throw new Error('Enter a valid source time zone, such as Asia/Shanghai.');
    if (!/^\d+$/.test(headerRow) || Number(headerRow) < 1 || Number(headerRow) > 30) throw new Error('Enter a header row between 1 and 30.');
    const fileBase64 = await readRiskFile(file);
    if (!active()) return;
    const data = await previewRiskOrders({ user_token: userToken, filename: file.name, file_base64: fileBase64, source_timezone: sourceTimezone.trim(), header_row: Number(headerRow), ...(sheetName.trim() ? { sheet_name: sheetName.trim() } : {}) });
    if (active()) setPreview(data);
  });
  const confirm = () => run('submit', async active => {
    if (!preview?.batch_id || !preview.confirm_token || Number(preview.summary?.ready) <= 0 || Date.parse(preview.expires_at) <= Date.now() || expired) return;
    const data = await submitRiskOrders({ user_token: userToken, batch_id: preview.batch_id, confirm_token: preview.confirm_token });
    if (!active()) return;
    setNotice(data.summary); setPreview(null); setPage(1); setRefresh(value => value + 1);
  });
  const systemTimezone = preview?.system_timezone || records.system_timezone;
  const ready = Number(preview?.summary?.ready) || 0;
  const canConfirm = Boolean(preview?.batch_id && preview.confirm_token && ready > 0 && Date.parse(preview.expires_at) > Date.now() && !expired);

  return <Modal open onClose={() => { if (!busyRef.current) onClose(); }} title={t('Import order results')} size="large"
    primaryAction={preview ? { content: t('Confirm import'), onAction: confirm, loading: busy === 'submit', disabled: Boolean(busy) || !canConfirm } : undefined}
    secondaryActions={[{ content: t('Close'), onAction: onClose, disabled: Boolean(busy) }]}>
    <Modal.Section><BlockStack gap="400">
      <Text as="p">{t('Import delivered and refused order results to improve COD risk management.')}</Text>
      <Text as="p" tone="subdued">{t('Imported results are used in this store. Only reviewed results are shared with other stores in this app.')}</Text>
      {error ? <Banner tone="critical">{t(error)}</Banner> : null}
      {notice ? <Banner tone="success"><BlockStack gap="200">
        <Text as="p">{t('Imported: {0}. Skipped: {1}.', [notice.accepted || 0, ['duplicate', 'stale', 'conflict', 'invalid'].reduce((sum, key) => sum + (Number(notice[key]) || 0), 0)])}</Text>
        {Number(notice.accepted) > 0 ? <Badge tone="attention">{t('Pending shared review')}</Badge> : null}
      </BlockStack></Banner> : null}
      <Text as="h3" variant="headingSm">{t('Download order template')}</Text>
      <Text as="p" tone="subdued">{t('Choose order dates, or leave both blank for the last 90 days. Up to 2,000 orders per file.')}</Text>
      <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
        <TextField type="date" label={t('Start date')} value={startDate} onChange={setStartDate} disabled={Boolean(busy)} />
        <TextField type="date" label={t('End date')} value={endDate} onChange={setEndDate} disabled={Boolean(busy)} />
      </InlineGrid>
      <InlineStack><Button onClick={download} loading={busy === 'download'} disabled={Boolean(busy)}>{t('Download order template')}</Button></InlineStack>
      <Text as="p" tone="subdued">{t('Fill in result, occurred_at and reason only. Keep all other columns unchanged.')}</Text>
      <Text as="p" tone="subdued">{t('Results: delivered, refused, unreceived, returned, in_transit or unknown. Dates: YYYY-MM-DD or YYYY-MM-DD HH:mm:ss.')}</Text>
      <div className="cod-risk-import-file"><label htmlFor="cod-risk-order-file">{t('Completed order file (CSV or XLSX)')}</label>
        <input id="cod-risk-order-file" type="file" accept=".csv,.xlsx" disabled={Boolean(busy)} onChange={chooseFile} />
      </div>
      <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
        <TextField label={t('Source time zone')} value={sourceTimezone} onChange={changeImport(setSourceTimezone)} placeholder="Asia/Shanghai" helpText={t('Use the time zone of the dates in your file.')} disabled={Boolean(busy)} autoComplete="off" />
        <TextField label={t('Header row')} type="number" min={1} max={30} value={headerRow} onChange={changeImport(setHeaderRow)} disabled={Boolean(busy)} autoComplete="off" />
        {/\.xlsx$/i.test(file?.name || '') ? <TextField label={t('Worksheet name (optional)')} value={sheetName} onChange={changeImport(setSheetName)} disabled={Boolean(busy)} autoComplete="off" /> : null}
      </InlineGrid>
      <InlineStack><Button onClick={createPreview} loading={busy === 'preview'} disabled={Boolean(busy) || !file}>{t('Preview import')}</Button></InlineStack>
      {systemTimezone ? <Text as="p" tone="subdued">{t('Times are shown in {0}. Date-only values retain their source date.', [systemTimezone])}</Text> : null}
      {preview ? <BlockStack gap="300">
        <Text as="h3" variant="headingSm">{t('Import preview')}</Text>
        <Text as="p">{t('Rows: {0}. Ready: {1}. Invalid: {2}. Duplicate: {3}. Conflict: {4}.', [preview.summary?.total || 0, ready, preview.summary?.invalid || 0, preview.summary?.duplicate || 0, preview.summary?.conflict || 0])}</Text>
        {Number(preview.summary?.stale) > 0 ? <Text as="p">{t('Outdated')}: {preview.summary.stale}</Text> : null}
        <Text as="p" tone="subdued">{t('Only ready rows will be imported. Other rows will be skipped.')}</Text>
        {expired ? <Banner tone="warning">{t('This preview has expired. Preview the file again before confirming.')}</Banner> : null}
        <div className="cod-risk-import-table"><DataTable headings={[t('Row'), t('Status'), t('Order'), t('Result'), t('Result date'), t('Details')]} rows={(preview.rows || []).map(row => [row.row, riskRowStatus(row.status), row.order_reference || '—', riskOutcomeLabel(row.outcome), riskImportTime(row, preview.system_timezone), t(row.message || row.warning || '—')])} /></div>
      </BlockStack> : null}
    </BlockStack></Modal.Section>
    <Modal.Section><BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="center"><Text as="h3" variant="headingSm">{t('Imported order results')}</Text><Button onClick={() => setRefresh(value => value + 1)} disabled={Boolean(busy) || recordsLoading}>{t('Refresh')}</Button></InlineStack>
      {historyError ? <Banner tone="critical">{t(historyError)}</Banner> : null}
      {recordsLoading ? <Text as="p">{t('Loading import history...')}</Text> : records.list?.length ? <div className="cod-risk-import-table"><DataTable
        headings={[t('Order'), t('Result'), t('Record category'), t('Result date'), t('Imported at'), t('Store use'), t('Sharing status')]}
        rows={records.list.map(row => [row.order_reference || '—', riskOutcomeLabel(row.outcome), riskPoolLabel(row.outcome, row.result_scope), riskImportTime(row, records.system_timezone), riskImportTime(row, records.system_timezone, true), row.local_active ? <Badge tone="success">{t('In use in this store')}</Badge> : t(row.review_status === 'invalidated' ? 'Invalid record' : 'Recorded'), <Badge tone={row.shared_active ? 'success' : row.review_status === 'pending' ? 'attention' : undefined}>{sharedReviewLabel(row)}</Badge>])} />
      </div> : <Text as="p" tone="subdued">{t('No imported order results yet.')}</Text>}
      <InlineStack align="end" gap="200"><Button onClick={() => setPage(value => value - 1)} disabled={Boolean(busy) || recordsLoading || page <= 1}>{t('Previous')}</Button><Text as="p">{t('Page {0} of {1}', [page, records.last_page || 1])}</Text><Button onClick={() => setPage(value => value + 1)} disabled={Boolean(busy) || recordsLoading || page >= records.last_page}>{t('Next')}</Button></InlineStack>
    </BlockStack></Modal.Section>
  </Modal>;
}
